import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import SlotDayCard from './components/SlotDayCard';
import { ReactComponent as OutlookIcon } from 'assets/icons/components/agenda/agenda-outlook.svg';
import InternalButton from 'components/InternalButton';
import { useUser } from 'global/UserContext';
import { useAvailabilitySlots } from 'hooks/useAvailabilitySlots';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { apiMain } from 'services/apiService';
import { AvailabilitySlot, CreateAvailabilitySlotDTO, Slot } from 'types/availabilitySlotTypes';
import { useIsMobile } from 'utils/mediaQueries';

const WEEK_DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

interface SyncStatus {
  status: 'idle' | 'syncing' | 'synced' | 'failed';
  message?: string;
}

const AgendaConfiguration: React.FC = () => {
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const userId = user?.id;

  const {
    slots,
    loading: slotsLoading,
    error: slotsError,
    addSlot,
    removeSlot,
    fetchSlots,
  } = useAvailabilitySlots(userId);

  const [modalSync, setModalSync] = React.useState<null | 'google' | 'outlook'>(null);
  const [syncLoading, setSyncLoading] = React.useState(false);
  const [syncStatus, setSyncStatus] = React.useState<{
    google: SyncStatus;
    outlook: SyncStatus;
  }>({
    google: { status: 'idle' },
    outlook: { status: 'idle' },
  });

  // Refs para controlar os intervalos de polling
  const googlePollingRef = React.useRef<NodeJS.Timeout | null>(null);
  const outlookPollingRef = React.useRef<NodeJS.Timeout | null>(null);

  // Converter AvailabilitySlot para Slot
  const convertToSlot = (availabilitySlot: AvailabilitySlot): Slot => ({
    id: availabilitySlot.id,
    user_id: availabilitySlot.user_id,
    weekday: availabilitySlot.weekday,
    start_time: availabilitySlot.start_time,
    end_time: availabilitySlot.end_time,
    slot_duration_minutes: availabilitySlot.slot_duration_minutes,
  });

  // Wrapper para converter Slot para CreateAvailabilitySlotDTO
  const handleAddSlot = async (slot: Slot) => {
    if (!userId) return;
    const createSlotDTO: CreateAvailabilitySlotDTO = {
      user_id: userId,
      weekday: slot.weekday,
      start_time: slot.start_time,
      end_time: slot.end_time,
      slot_duration_minutes: slot.slot_duration_minutes,
    };
    await addSlot(createSlotDTO);
  };

  // Wrapper para removeSlot
  const handleRemoveSlot = (slotId: string) => {
    removeSlot(slotId).catch(error => {
      console.error('Erro ao remover slot:', error);
    });
  };

  const getAuthUrl = async (provider: 'google' | 'outlook'): Promise<string> => {
    if (!userId) throw new Error('Usuário não identificado');

    const endpoint =
      provider === 'google'
        ? `/meetings/v1/calendar-sync/google/oauth2/${userId}`
        : `/meetings/v1/calendar-sync/outlook/oauth2/${userId}`;

    const response = await apiMain.get(endpoint);
    return response.data;
  };

  const checkSyncStatus = async (provider: 'google' | 'outlook'): Promise<boolean> => {
    if (!userId) return false;

    try {
      const response = await apiMain.get(`/meetings/v1/calendar-sync/config/status/${userId}`);
      const statuses = response.data;

      const providerStatus = statuses.find(
        (status: any) => status.provider === provider.toUpperCase()
      );

      if (providerStatus && ['connected', 'synced', 'expired'].includes(providerStatus.status)) {
        return true;
      }

      return false;
    } catch (error) {
      console.error(`Erro ao verificar status do ${provider}:`, error);
      return false;
    }
  };

  // Função para iniciar polling de status
  const startStatusPolling = (provider: 'google' | 'outlook') => {
    const pollingRef = provider === 'google' ? googlePollingRef : outlookPollingRef;
    const startTime = Date.now();
    const maxDuration = 30 * 60 * 1000; // 30 minutos
    const interval = 5 * 1000; // 5 segundos

    const poll = async () => {
      // Verifica se já passou dos 30 minutos
      if (Date.now() - startTime > maxDuration) {
        setSyncStatus(prev => ({
          ...prev,
          [provider]: { status: 'failed', message: 'Tempo limite excedido' },
        }));
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        return;
      }

      const isConnected = await checkSyncStatus(provider);

      if (isConnected) {
        setSyncStatus(prev => ({
          ...prev,
          [provider]: { status: 'synced', message: 'Sincronização concluída' },
        }));
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    };

    poll();

    pollingRef.current = setInterval(poll, interval);
  };

  const stopStatusPolling = (provider: 'google' | 'outlook') => {
    const pollingRef = provider === 'google' ? googlePollingRef : outlookPollingRef;
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleSync = (type: 'google' | 'outlook') => {
    setModalSync(type);
  };

  const confirmSync = async () => {
    if (!modalSync || !userId) return;

    setSyncLoading(true);
    setSyncStatus(prev => ({
      ...prev,
      [modalSync]: { status: 'syncing', message: 'Iniciando sincronização...' },
    }));

    try {
      const authUrl = await getAuthUrl(modalSync);

      window.open(authUrl, '_blank');

      // Inicia o polling de status
      startStatusPolling(modalSync);

      setModalSync(null);
    } catch (error) {
      console.error('Erro ao iniciar sincronização:', error);
      setSyncStatus(prev => ({
        ...prev,
        [modalSync]: { status: 'failed', message: 'Erro ao iniciar sincronização' },
      }));
    } finally {
      setSyncLoading(false);
    }
  };

  // Cleanup dos intervalos quando o componente desmonta
  React.useEffect(() => {
    const isSynced = async () => {
      const googleStatus = await checkSyncStatus('google');
      const outlookStatus = await checkSyncStatus('outlook');
      setSyncStatus(prev => ({
        ...prev,
        google: { status: googleStatus ? 'synced' : 'idle' },
        outlook: { status: outlookStatus ? 'synced' : 'idle' },
      }));
    };
    isSynced();

    return () => {
      stopStatusPolling('google');
      stopStatusPolling('outlook');
    };
  }, []);

  const getButtonText = (provider: 'google' | 'outlook') => {
    const status = syncStatus[provider];
    switch (status.status) {
      case 'synced':
        return 'Sincronizada';
      case 'syncing':
        return 'Sincronizando...';
      case 'failed':
        return 'Falhou';
      default:
        return 'Sincronizar';
    }
  };

  const getButtonColor = (provider: 'google' | 'outlook') => {
    const status = syncStatus[provider];
    switch (status.status) {
      case 'synced':
        return theme.palette.success.main;
      case 'failed':
        return theme.palette.error.main;
      default:
        return theme.palette.text.primary;
    }
  };

  const getButtonIcon = (provider: 'google' | 'outlook') => {
    const status = syncStatus[provider];
    switch (status.status) {
      case 'syncing':
        return <span className="loader" style={{ marginRight: 4 }} />;
      case 'synced':
        return <CheckCircleIcon color="success" sx={{ p: 0.3 }} />;
      case 'failed':
        return <RefreshIcon sx={{ p: 0.3, color: theme.palette.error.main }} />;
      default:
        return <RefreshIcon sx={{ p: 0.3 }} />;
    }
  };

  return (
    <Box>
      {/* <FormControl
            fullWidth
            variant="outlined"
            sx={{ width: isMobile ? '100%' : '60%', mb: 3 }}
          >
            <InputLabel id="recurrence-label">Recorrência</InputLabel>
            <Select
              labelId="recurrence-label"
              value={recurrence}
              label="Recorrência"
              onChange={e => setRecurrence(e.target.value as 'none' | 'weekly' | 'custom')}
            >
              <MenuItem value="none" disabled>
                Não repetir
              </MenuItem>
              <MenuItem value="weekly">Repetir semanalmente</MenuItem>
              <MenuItem value="custom" disabled>
                Personalizar
              </MenuItem>
            </Select>
          </FormControl> */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Definir Disponibilidade
      </Typography>

      {/* NOVA ESTRUTURA: Stack principal para alinhar lista de dias e cards de sincronização */}
      <Stack direction={{ xs: 'column-reverse', md: 'row' }} alignItems="flex-start" spacing={2}>
        {/* Lista de dias */}
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: 'fit-content' },
          }}
        >
          <Stack spacing={2}>
            {WEEK_DAYS.map((label, index) => (
              <SlotDayCard
                key={index}
                label={label}
                weekday={index}
                slots={slots.filter(slot => slot.weekday === index).map(convertToSlot)}
                onAddSlot={handleAddSlot}
                onRemoveSlot={handleRemoveSlot}
              />
            ))}
          </Stack>
        </Box>
        {/* Cards de sincronização */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            width: { xs: '100%', md: 'auto' },
            minWidth: { md: 480 }, // Ajuste conforme necessário para os cards
            flex: 'none',
          }}
        >
          {/* Outlook Card */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 2,
              p: 2,
              width: { xs: '48%', sm: '48%', md: 220, lg: 240 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: { xs: 2, sm: 0 },
              cursor: syncStatus.outlook.status === 'synced' ? 'default' : 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: syncStatus.outlook.status === 'synced' ? 'none' : 'translateY(-2px)',
                boxShadow: syncStatus.outlook.status === 'synced' ? 2 : 4,
                bgcolor:
                  syncStatus.outlook.status === 'synced' ? 'background.paper' : 'action.hover',
              },
            }}
            onClick={() => (syncStatus.outlook.status === 'synced' ? null : handleSync('outlook'))}
          >
            <Box
              sx={{
                mb: 1,
                width: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <OutlookIcon style={{ width: '100%', height: '100%' }} />
            </Box>
            <InternalButton
              background={getButtonColor('outlook')}
              textColor={theme.palette.text.primary}
              icon={getButtonIcon('outlook')}
              size="small"
              onClick={() => {}}
              disabled={syncStatus.outlook.status === 'syncing'}
              variant="outline"
            >
              Agenda Outlook - {getButtonText('outlook')}
            </InternalButton>
          </Box>
          {/* Google Card */}
          {/*   <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 2,
              p: 2,
              width: { xs: '48%', sm: '48%', md: 220, lg: 240 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: { xs: 2, sm: 0 },
              cursor: syncStatus.google.status === 'synced' ? 'default' : 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: syncStatus.google.status === 'synced' ? 'none' : 'translateY(-2px)',
                boxShadow: syncStatus.google.status === 'synced' ? 2 : 4,
                bgcolor:
                  syncStatus.google.status === 'synced' ? 'background.paper' : 'action.hover',
              },
            }}
            onClick={() => (syncStatus.google.status === 'synced' ? null : handleSync('google'))}
          >
            <Box
              sx={{
                mb: 1,
                width: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GoogleIcon style={{ width: '100%', height: '100%' }} />
            </Box>
            <InternalButton
              background={getButtonColor('google')}
              textColor={theme.palette.text.primary}
              icon={getButtonIcon('google')}
              size="small"
              onClick={() => {}}
              disabled={syncStatus.google.status === 'syncing'}
              variant="outline"
            >
              Agenda Google - {getButtonText('google')}
            </InternalButton>
          </Box> */}
        </Stack>
      </Stack>

      <GlobalModal
        open={!!modalSync}
        title={modalSync === 'google' ? 'Sincronizar com Google' : 'Sincronizar com Outlook'}
        description={`Deseja sincronizar sua agenda ${modalSync === 'google' ? 'Google' : 'Outlook'}? Você será redirecionado para autorizar o acesso.`}
        onClose={() => setModalSync(null)}
        onCancel={() => setModalSync(null)}
        onConfirm={confirmSync}
        confirmButtonText={syncLoading ? 'Sincronizando...' : 'Sim, sincronizar'}
        cancelButtonText="Cancelar"
        confirmDisabled={syncLoading}
        cancelDisabled={syncLoading}
      />
    </Box>
  );
};

export default AgendaConfiguration;
